import React from 'react';
import {FormattedMessage} from 'react-intl';
import {MODAL_TYPE, showModal} from '../components/modals/generic_modal_conductor';
import {createToast} from '../components/toasts/another-toast/toaster';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import UpdateCompanyMutation from '../../../mutations/update_company_mutation.modern';
import UpdateProjectMutation from '../../../mutations/update_project_mutation.modern';
import Util from './util';
import DirectApi from '../../../directApi';
import UpdateJiraCloudSyncSettings from '../../../mutations/jira/cloud/update_jira_cloud_sync_settings_mutation';
import UpdateJiraServerSyncSettings from '../../../mutations/jira/server/update_jira_server_sync_settings_mutation';

const JIRA_PREFIX = {
	NONE: 'NONE',
	SIMPLE: 'SIMPLE',
	FULL: 'FULL',
};

async function fetchJiraBoards(jiraProjectId, deep, jiraType) {
	try {
		return await DirectApi.Fetch(
			`jiraBoards?type=${jiraType}${jiraProjectId ? `&projectId=${jiraProjectId}` : ''}&deep=${deep}`
		).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

async function fetchJiraCustomFields(jiraType, jiraProjectStyle) {
	try {
		return await DirectApi.Fetch(`jirafields/${jiraType}?projectType=${jiraProjectStyle}`).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

async function fetchIssuesCountForJiraProject(jiraProjectId, type, cutoffDate) {
	try {
		return await DirectApi.Fetch(
			`jira/project_issues_count/${type}/${jiraProjectId}${
				cutoffDate ? `?cutoffDate=${cutoffDate.format('YYYY-MM-DD')}` : ''
			}`
		).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

async function fetchEpicsCountForJiraProject(jiraProjectId, type, cutoffDate) {
	try {
		return await DirectApi.Fetch(
			`jira/project_epics_count/${type}/${jiraProjectId}${
				cutoffDate ? `?cutoffDate=${cutoffDate.format('YYYY-MM-DD')}` : ''
			}`
		).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

async function fetchIssuesCountForJiraEpics(jiraEpicIds, type, cutoffDate) {
	try {
		return await DirectApi.Fetch(
			`jira/epic_issues_count/${type}?epicIds=${jiraEpicIds.join(',')}${
				cutoffDate ? `&cutoffDate=${cutoffDate.format('YYYY-MM-DD')}` : ''
			}`
		).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

async function fetchUsersCountForJiraProject(jiraProjectId, type) {
	try {
		return await DirectApi.Fetch(`jira/project_teammembers_count/${type}/${jiraProjectId}`).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

async function fetchSprintsCountForJiraBoard(boardId, type, cutoffDate) {
	try {
		return await DirectApi.Fetch(
			`jira/board_sprints_count/${type}/${boardId}${cutoffDate ? `?cutoffDate=${cutoffDate.format('YYYY-MM-DD')}` : ''}`
		).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

async function fetchVersionsCountForJiraProject(jiraProjectId, type, cutoffDate) {
	try {
		return await DirectApi.Fetch(
			`jira/project_versions_count/${type}/${jiraProjectId}${
				cutoffDate ? `?cutoffDate=${cutoffDate.format('YYYY-MM-DD')}` : ''
			}`
		).then(json => {
			return json;
		});
	} catch (error) {
		return error;
	}
}

function unlinkProject(project) {
	const deactivateForReal = () => {
		Util.CommitMutation(UpdateProjectMutation, {
			project,
			jiraProjectId: '',
			unsynchedJiraProject: true,
		});
	};

	showModal({
		type: MODAL_TYPE.WARNING,
		warningMessageId: 'settings.unlink_jira_warning_title',
		warningInformation: [
			<div className="whitespace-pre">
				<FormattedMessage id="settings.unlink_jira_warning" />
			</div>,
		],
		buttons: [
			{
				text: <FormattedMessage id="common.cancel" />,
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.WHITE,
			},
			{
				text: <FormattedMessage id="common.unlink" />,
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.RED,
				callback: deactivateForReal,
			},
		],
	});
}

function changeJiraPrefixType(jiraPrefixType, companyId, isServer) {
	const onConfirm = () => {
		const onSuccess = () => {
			createToast({duration: 5000, message: 'Jira prefix settings changed'});
		};
		Util.CommitMutation(
			UpdateCompanyMutation,
			{
				id: companyId,
				jiraPrefixType: isServer ? undefined : jiraPrefixType,
				jiraServerPrefixType: isServer ? jiraPrefixType : undefined,
			},
			onSuccess
		);
	};
	openWarningModal('app_jira.change_prefix.confirm', 'app_jira.change_prefix.description', onConfirm);
}

function toggleJiraCloudToForecastOneWaySync(checked) {
	const onConfirm = () => {
		const onSuccess = () => {
			createToast({duration: 5000, message: 'Jira sync settings changed'});
		};
		Util.CommitMutation(UpdateJiraCloudSyncSettings, {isJiraToForecastOneWaySync: checked ? 'true' : 'false'}, onSuccess);
	};
	openWarningModal('app_jira.change_sync_setting.confirm', 'app_jira.change_sync_setting.description', onConfirm);
}

function toggleJiraServerToForecastOneWaySync(checked) {
	const onConfirm = () => {
		const onSuccess = () => {
			createToast({duration: 5000, message: 'Jira sync settings changed'});
		};
		Util.CommitMutation(UpdateJiraServerSyncSettings, {isJiraToForecastOneWaySync: checked ? 'true' : 'false'}, onSuccess);
	};
	openWarningModal('app_jira.change_sync_setting.confirm', 'app_jira.change_sync_setting.description', onConfirm);
}

function openWarningModal(warningMessageId, warningInformationId, onConfirm, onCancel) {
	showModal({
		type: MODAL_TYPE.WARNING,
		warningMessageId: warningMessageId,
		warningInformation: [<FormattedMessage id={warningInformationId} />],
		buttons: [
			{
				text: <FormattedMessage id="common.cancel" />,
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.WHITE,
				callback: onCancel,
			},
			{
				text: <FormattedMessage id="common.ok" />,
				callback: onConfirm,
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.GREEN,
			},
		],
	});
}
module.exports = {
	JIRA_PREFIX,
	fetchJiraBoards,
	fetchJiraCustomFields,
	fetchIssuesCountForJiraProject,
	fetchEpicsCountForJiraProject,
	fetchIssuesCountForJiraEpics,
	fetchUsersCountForJiraProject,
	fetchSprintsCountForJiraBoard,
	fetchVersionsCountForJiraProject,
	unlinkProject,
	changeJiraPrefixType,
	toggleJiraCloudToForecastOneWaySync,
	toggleJiraServerToForecastOneWaySync,
};
